import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
  Item,
  Category,
  PaginatedResponse,
} from "@/shared/types";
import type {
  ItemDetailsResponse,
  GetItemsParams,
  HelperDataResponse,
  ItemsListResponse,
  ImportItemsRequest,
  ImportItemsResponse,
  CategoryResponse,
  DraftResponse,
  CreateItemFormValues,
  UpdateItemPayload,
} from "@/app/types/items.types";

import {
  mapApiItemToItem,
  mapApiItemDetailToItem,
} from "@/features/items/utils/items.utils";

export interface UpdateItemRequest {
  data: UpdateItemPayload;
  id: string | number;
}

// type NameByIdResponse<T extends { id: string; name: string }> = {
//   items: T[];
//   nameById: Record<string, string>;
// };

// const createNameByIdRecord = <T extends { id: string; name: string }>(
//   items: T[],
// ) => {
//   return Object.fromEntries(items.map((item) => [item.id, item.name]));
// };



// Items API endpoints
export const itemsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getItems: builder.query<PaginatedResponse<Item>, GetItemsParams>({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sku = "",
        category_ids,
        subcategory_ids,
        tag_ids,
        status,
        supplier_ids,
        purchase_uom_ids,
        base_uom_ids,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("per_page", String(limit));
        queryParams.set("page", String(page));
        queryParams.set("item_type_id", "1");

        if (search) {
          queryParams.set("search", search);
        }
        if (sku) {
          queryParams.set("sku", sku);
        }
        if (status) {
          queryParams.set("status", status);
        }
        const addArrayParams = (key: string, values?: number[]) => {
          if (values && values.length > 0) {
            values.forEach((value, index) => {
              queryParams.append(`${key}[${index}]`, String(value));
            });
          }
        };

        addArrayParams("category_ids", category_ids);
        addArrayParams("subcategory_ids", subcategory_ids);
        addArrayParams("tag_ids", tag_ids);
        addArrayParams("supplier_ids", supplier_ids);
        addArrayParams("purchase_uom_ids", purchase_uom_ids);
        addArrayParams("base_uom_ids", base_uom_ids);

        return `${Endpoints.items.getAllItems}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ItemsListResponse>,
      ): PaginatedResponse<Item> => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load items");
        }

        const itemsMeta = response.data.items;
        const draftItems = Array.isArray(response.data.draft_items)
          ? (response.data.draft_items as Item[])
          : [];
        return {
          data: (itemsMeta.data || []).map(mapApiItemToItem),
          draftItems: draftItems,
          pagination: {
            page: itemsMeta.current_page,
            limit: itemsMeta.per_page,
            total: itemsMeta.total,
            totalPages: itemsMeta.total_pages,
          },
        };
      },
      providesTags: ["Items"],
    }),

    getItemById: builder.query<Item, string | number>({
      query: (id) => Endpoints.items.getItemById(id),
      transformResponse: (response: ApiResponse<ItemDetailsResponse>): Item => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load item");
        }
        return mapApiItemDetailToItem(response.data);
      },
      providesTags: (_result, _error, id) => [{ type: "Item", id: String(id) }],
      keepUnusedDataFor: 0,
    }),

    createItems: builder.mutation<
      CreateItemFormValues,
      { data: CreateItemFormValues }
    >({
      query: ({ data }) => ({
        url: Endpoints.items.createItems,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return [{ type: "Items", id: "List" }];
        return [];
      },
    }),

    updateItem: builder.mutation<UpdateItemPayload, UpdateItemRequest>({
      query: ({ data, id }) => ({
        url: Endpoints.items.updateItems(id),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, { id }) => {
        if (!result) return [];
        return [
          "Items",
          { type: "Items", id: "List" },
          ...(id ? [{ type: "Item" as const, id }] : []),
        ];
      },
    }),

    deleteItem: builder.mutation<void, number[]>({
      query: (itemIds) => {
        const queryParams = new URLSearchParams();
        itemIds.forEach((id, index) => {
          queryParams.append(`item_ids[${index}]`, String(id));
        });
        return {
          url: `${Endpoints.items.deleteItems}?${queryParams.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Items"],
    }),

    changeItemStatus: builder.mutation<
      ApiResponse<Item>,
      { item_ids: number[]; status: "active" | "inactive" }
    >({
      query: ({ item_ids, status }) => ({
        url: `${Endpoints.items.changeStatus}`,
        method: "PUT",
        body: { item_ids, status },
      }),
      invalidatesTags: ["Items"],
    }),

    getCategories: builder.query<
      ApiResponse<CategoryResponse[]>,
      { item_type_id?: number } | void
    >({
      query: (params) => {
        const queryParams = params?.item_type_id
          ? `?item_type_id=${params.item_type_id}`
          : "";
        return `${Endpoints.categories.getCategories}${queryParams}`;
      },
      transformResponse: (
        response: ApiResponse<CategoryResponse[]>,
      ): ApiResponse<CategoryResponse[]> => {
        return response;
      },
      providesTags: ["Categories"],
    }),

    getHelperData: builder.query<ApiResponse<HelperDataResponse>, void>({
      query: () => `${Endpoints.items.getHelperData}`,
      transformResponse: (
        response: ApiResponse<HelperDataResponse>,
      ): ApiResponse<HelperDataResponse> => {
        return response;
      },
      providesTags: [{ type: "Items", id: "HelperData" }],
    }),

    importItems: builder.mutation<ImportItemsResponse, ImportItemsRequest>({
      query: (body) => ({
        url: Endpoints.items.importItems,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Items"],
    }),
    showDraft: builder.query<ApiResponse<DraftResponse>, string>({
      query: (id) => `${Endpoints.draft.showDraft}/${id}`,
      transformResponse: (
        response: ApiResponse<DraftResponse>,
      ): ApiResponse<DraftResponse> => {
        return response;
      },
      providesTags: ["Draft"],
    }),
    updateDraft: builder.mutation<ApiResponse<DraftResponse>, { id: string; data: CreateItemFormValues }>({
      query: ({ id, data }) => ({
        url: `${Endpoints.draft.updateDraft}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Draft"],
    }),
  }),
});

export const {
  useGetItemsQuery,
  useGetItemByIdQuery,
  useCreateItemsMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useChangeItemStatusMutation,
  useImportItemsMutation,
  useGetCategoriesQuery,
  useGetHelperDataQuery,
  useShowDraftQuery,
  useUpdateDraftMutation,
} = itemsApi;
